<template>
  <div>
    <!-- Created dates are derived from status history; say so when it is absent. -->
    <v-alert v-if="dataLoaded && !hasStatusHistory"
             type="info"
             variant="tonal"
             density="compact"
             class="mb-4">
      {{ $t('noStatusHistoryWarning') }}
    </v-alert>

    <!-- KPIs. The three due buckets are mutually exclusive and clickable. -->
    <v-row>
      <v-col cols="12" sm="6" md="3">
        <v-skeleton-loader v-if="!dataLoaded" type="card" height="120" />
        <KpiCard v-else
                 :label="$t('totalAssets')"
                 :value="totals.assets"
                 :tooltip="$t('totalAssetsTooltip')" />
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-skeleton-loader v-if="!dataLoaded" type="card" height="120" />
        <KpiCard v-else
                 :label="$t('lateOverdue')"
                 :value="totals.overdue"
                 :color="overdueColor"
                 :tooltip="$t('lateOverdueTooltip')"
                 clickable
                 :active="isSelected('overdue')"
                 @select="selectBucket('overdue')" />
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-skeleton-loader v-if="!dataLoaded" type="card" height="120" />
        <KpiCard v-else
                 :label="$t('due30Days')"
                 :value="totals.due30"
                 clickable
                 :active="isSelected('due30')"
                 @select="selectBucket('due30')" />
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-skeleton-loader v-if="!dataLoaded" type="card" height="120" />
        <KpiCard v-else
                 :label="$t('due31to60Days')"
                 :value="totals.due60"
                 :tooltip="$t('due31to60Tooltip')"
                 clickable
                 :active="isSelected('due60')"
                 @select="selectBucket('due60')" />
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="8">
        <v-skeleton-loader v-if="!dataLoaded" type="card" height="480" />
        <InspectionProgressChart v-else />
      </v-col>
      <v-col cols="12" md="4">
        <v-skeleton-loader v-if="!dataLoaded" type="card" height="420" />
        <InspectionsDueChart v-else />
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="4">
        <v-skeleton-loader v-if="!dataLoaded" type="card" height="440" />
        <PlannedInspectedBarChart v-else group-by="drawing" />
      </v-col>
      <v-col cols="12" md="4">
        <v-skeleton-loader v-if="!dataLoaded" type="card" height="440" />
        <PlannedInspectedBarChart v-else group-by="category" />
      </v-col>
      <v-col cols="12" md="4">
        <v-skeleton-loader v-if="!dataLoaded" type="card" height="440" />
        <PlannedInspectedBarChart v-else group-by="type" />
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-skeleton-loader v-if="!dataLoaded" type="card" height="460" />
        <InspectionIntervalBoxPlot v-else />
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-skeleton-loader v-if="!dataLoaded" type="card" height="600" />
        <InspectionScheduleGantt v-else />
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <AssetsGrid preset="inspection" />
      </v-col>
    </v-row>
  </div>
</template>

<script setup>
import { computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import KpiCard from '@/components/cards/KpiCard.vue';
import InspectionProgressChart from '@/components/charts/InspectionProgressChart.vue';
import InspectionsDueChart from '@/components/charts/InspectionsDueChart.vue';
import PlannedInspectedBarChart from '@/components/charts/PlannedInspectedBarChart.vue';
import InspectionIntervalBoxPlot from '@/components/charts/InspectionIntervalBoxPlot.vue';
import InspectionScheduleGantt from '@/components/gantts/InspectionScheduleGantt.vue';
import AssetsGrid from '@/components/grids/AssetsGrid.vue';
import { useKpiQuery, num } from '@/composables/useKpiQuery';
import { useSelectableFilter } from '@/composables/useSelectableFilter';
import { statusColors } from '@/utils/vizPalette';

const store = useStore();
const { t } = useI18n();

const dataLoaded = computed(() => store.getters.getDataLoaded);
const hasStatusHistory = computed(() => store.getters.getAssetModel.hasStatusHistory);
const overdueColor = statusColors.serious;

// Every bucket is derived from the same mutually exclusive CASE in the
// filteredAssets view, so the counts can never double-count an asset.
const { totals } = useKpiQuery(
  `
    SELECT
      COUNT(*) AS assets,
      COUNT(*) FILTER (WHERE dueBucket = 'overdue') AS overdue,
      COUNT(*) FILTER (WHERE dueBucket = 'due30') AS due30,
      COUNT(*) FILTER (WHERE dueBucket = 'due60') AS due60
    FROM filteredAssets
  `,
  (row) => ({
    assets: num(row.assets),
    overdue: num(row.overdue),
    due30: num(row.due30),
    due60: num(row.due60),
  })
);

const BUCKET_LABELS = {
  overdue: 'lateOverdue',
  due30: 'due30Days',
  due60: 'due31to60Days',
};

const { isSelected, sync, toggle } = useSelectableFilter('dueBucketKpi');

const selectBucket = async (bucket) => {
  await toggle(bucket, `dueBucket = '${bucket}'`, t(BUCKET_LABELS[bucket]));
};

// Drop the highlight when another visual clears the context.
watch(
  () => store.state.filterContext?.filtersLastUpdated?.value,
  () => sync(),
  { immediate: true }
);
</script>
